import type { ExtensionParams, Parameters } from '@atlaskit/editor-common/extensions';

import { expVal } from '@confluence/feature-experiments';

function getParameterValue(parameters: Record<string, unknown>, propPathArray: string[]): unknown {
	let value: unknown = parameters;
	for (const prop of propPathArray) {
		if (!value || typeof value !== 'object' || !(prop in value)) {
			return undefined;
		}
		value = (value as Record<string, unknown>)[prop];
	}
	return value;
}

/**
 * Determines if an extension should be displayed as inline based on Statsig Dynamic Config.
 *
 * Statsig Dynamic Config format: Array of strings like "path.to.property=expectedValue"
 *
 * @param extensionParams - Extension parameters to check against Statsig Dynamic Config
 * @returns true if any config rule matches, false otherwise
 */
export function shouldDisplayExtensionAsInline<T extends Parameters = Parameters>(
	extensionParams?: ExtensionParams<T>,
): boolean {
	try {
		if (!extensionParams?.parameters) {
			return false;
		}

		// eslint-disable-next-line confluence-feature-gating/inline-usage, confluence-feature-gating/inline-expVal-params
		const configArray = expVal<string[]>('platform_editor_extension_as_inline_config', 'value', []);

		if (!Array.isArray(configArray)) {
			return false;
		}

		for (const config of configArray) {
			if (!config || typeof config !== 'string') {
				continue;
			}

			const [propPath, value] = config.split('=');
			if (!propPath || !value) {
				continue;
			}

			const propPathArray = propPath.split('.').filter(Boolean);
			if (propPathArray.length === 0) {
				continue;
			}

			const parameterValue = getParameterValue(extensionParams.parameters, propPathArray);
			if (parameterValue === value) {
				return true;
			}
		}
	} catch {}

	// if we get here, no config rule matched or there was an error
	return false;
}
